import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'crypto',
  version: '1.0.0',
  properties: [
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'hash',
      options: [
        { value: 'hash', name: 'Hash' },
        { value: 'hmac', name: 'HMAC' },
        { value: 'encrypt', name: 'Encrypt' },
        { value: 'decrypt', name: 'Decrypt' },
        { value: 'sign', name: 'Sign' },
        { value: 'generateKey', name: 'Generate Key' },
        { value: 'verifyJwt', name: 'Verify JWT' },
      ],
      description: 'The cryptographic action to perform',
    },
    {
      key: 'value',
      label: 'Value',
      type: 'string',
      required: true,
      placeholder: 'e.g., {{ $json.data }} or text to process',
      description: 'The value to hash, encrypt, decrypt, or sign',
      displayOptions: {
        show: {
          action: ['hash', 'hmac', 'encrypt', 'decrypt', 'sign'],
        },
      },
    },
    {
      key: 'algorithm',
      label: 'Hash Algorithm',
      type: 'options',
      required: false,
      default: 'sha256',
      options: [
        { value: 'md5', name: 'MD5' },
        { value: 'sha1', name: 'SHA-1' },
        { value: 'sha256', name: 'SHA-256' },
        { value: 'sha384', name: 'SHA-384' },
        { value: 'sha512', name: 'SHA-512' },
      ],
      description: 'The hash algorithm to use',
      displayOptions: {
        show: {
          action: ['hash', 'hmac', 'sign'],
        },
      },
    },
    {
      key: 'secretKey',
      label: 'Secret Key',
      type: 'string',
      required: true,
      placeholder: 'Enter your secret key',
      description: 'The secret key for HMAC, encryption, decryption, or signing',
      displayOptions: {
        show: {
          action: ['hmac', 'encrypt', 'decrypt', 'sign'],
        },
      },
    },
    {
      key: 'encryptionAlgorithm',
      label: 'Encryption Algorithm',
      type: 'options',
      required: false,
      default: 'aes-256-cbc',
      options: [
        { value: 'aes-256-cbc', name: 'AES-256-CBC' },
      ],
      description: 'The encryption algorithm to use',
      displayOptions: {
        show: {
          action: ['encrypt', 'decrypt'],
        },
      },
    },
    {
      key: 'encoding',
      label: 'Output Encoding',
      type: 'options',
      required: false,
      default: 'hex',
      options: [
        { value: 'hex', name: 'Hexadecimal' },
        { value: 'base64', name: 'Base64' },
        { value: 'binary', name: 'Binary' },
      ],
      description: 'The encoding format for the output',
      displayOptions: {
        show: {
          action: ['hash', 'hmac', 'sign', 'generateKey'],
        },
      },
    },
    {
      key: 'keyLength',
      label: 'Key Length (bytes)',
      type: 'number',
      required: false,
      default: 32,
      placeholder: '32 (256 bits)',
      description: 'The length of the key to generate in bytes (32 = 256 bits)',
      displayOptions: {
        show: {
          action: ['generateKey'],
        },
      },
    },
    // JWT Verification properties
    {
      key: 'jwtToken',
      label: 'JWT Token',
      type: 'string',
      required: true,
      placeholder: 'e.g., {{ $json.token }} or eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...',
      description: 'The JWT token to verify',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
        },
      },
    },
    {
      key: 'jwtSecretOrKey',
      label: 'Secret / Public Key',
      type: 'string',
      required: true,
      placeholder: 'Secret for HMAC or PEM public key for RSA/ECDSA',
      description: 'The secret key (for HS256/384/512) or PEM-encoded public key (for RS256/384/512, ES256/384/512)',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
        },
      },
    },
    {
      key: 'jwtAlgorithm',
      label: 'Algorithm',
      type: 'options',
      required: false,
      default: 'RS256',
      options: [
        { value: 'RS256', name: 'RS256 (RSA + SHA-256)' },
        { value: 'RS384', name: 'RS384 (RSA + SHA-384)' },
        { value: 'RS512', name: 'RS512 (RSA + SHA-512)' },
        { value: 'HS256', name: 'HS256 (HMAC + SHA-256)' },
        { value: 'HS384', name: 'HS384 (HMAC + SHA-384)' },
        { value: 'HS512', name: 'HS512 (HMAC + SHA-512)' },
        { value: 'ES256', name: 'ES256 (ECDSA + SHA-256)' },
        { value: 'ES384', name: 'ES384 (ECDSA + SHA-384)' },
        { value: 'ES512', name: 'ES512 (ECDSA + SHA-512)' },
      ],
      description: 'The algorithm used to sign the JWT',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
        },
      },
    },
    {
      key: 'jwtValidateLifetime',
      label: 'Validate Expiration',
      type: 'boolean',
      required: false,
      default: true,
      description: 'Check if the token has expired (validates exp and nbf claims)',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
        },
      },
    },
    {
      key: 'jwtValidateIssuer',
      label: 'Validate Issuer',
      type: 'boolean',
      required: false,
      default: false,
      description: 'Validate the token issuer (iss claim)',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
        },
      },
    },
    {
      key: 'jwtIssuer',
      label: 'Expected Issuer',
      type: 'string',
      required: false,
      placeholder: 'e.g., https://auth.example.com',
      description: 'The expected issuer value to validate against',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
          jwtValidateIssuer: [true],
        },
      },
    },
    {
      key: 'jwtValidateAudience',
      label: 'Validate Audience',
      type: 'boolean',
      required: false,
      default: false,
      description: 'Validate the token audience (aud claim)',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
        },
      },
    },
    {
      key: 'jwtAudience',
      label: 'Expected Audience',
      type: 'string',
      required: false,
      placeholder: 'e.g., my-api',
      description: 'The expected audience value to validate against',
      displayOptions: {
        show: {
          action: ['verifyJwt'],
          jwtValidateAudience: [true],
        },
      },
    },
  ],
};

export default schema;
